import React, { useState } from 'react';
import { FileIcon, DownloadIcon, ExternalLinkIcon, EyeIcon, FileTextIcon, ImageIcon, FileSpreadsheetIcon, FileCode2Icon, FileArchiveIcon, FileAudioIcon, FileVideoIcon, LinkIcon, CalendarIcon, UserIcon, TagIcon, FolderIcon, ArrowUpIcon, ArrowDownIcon, ChevronUpIcon, ChevronDownIcon, FolderTreeIcon, FileSymlinkIcon, DatabaseIcon, ShieldIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, MailIcon } from 'lucide-react';
export function FileGrid({
  files,
  onFileSelect,
  onDownload,
  isLoading,
  locale = 'en-US',
  isAdminView = false,
  viewMode = 'list',
  formatFileSize
}) {
  const [sortField, setSortField] = useState('uploadDate');
  const [sortDirection, setSortDirection] = useState('desc');
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get file type icon
  const getFileTypeIcon = fileType => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileTextIcon className="h-4 w-4 text-red-500" data-id="element-5659" />;
      case 'docx':
      case 'doc':
        return <FileTextIcon className="h-4 w-4 text-blue-500" data-id="element-5660" />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheetIcon className="h-4 w-4 text-green-500" data-id="element-5661" />;
      case 'pptx':
      case 'ppt':
        return <FileIcon className="h-4 w-4 text-orange-500" data-id="element-5662" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'tiff':
        return <ImageIcon className="h-4 w-4 text-purple-500" data-id="element-5663" />;
      case 'mp3':
      case 'wav':
      case 'ogg':
        return <FileAudioIcon className="h-4 w-4 text-yellow-500" data-id="element-5664" />;
      case 'mp4':
      case 'mov':
      case 'avi':
        return <FileVideoIcon className="h-4 w-4 text-pink-500" data-id="element-5665" />;
      case 'zip':
      case 'rar':
      case '7z':
        return <FileArchiveIcon className="h-4 w-4 text-gray-500" data-id="element-5666" />;
      case 'js':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return <FileCode2Icon className="h-4 w-4 text-indigo-500" data-id="element-5667" />;
      case 'eml':
        return <MailIcon className="h-4 w-4 text-blue-400" data-id="element-5668" />;
      default:
        return <FileIcon className="h-4 w-4 text-gray-500" data-id="element-5669" />;
    }
  };
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort files based on current sort settings
  const sortedFiles = [...(files || [])].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'size':
        comparison = a.size - b.size;
        break;
      case 'uploadDate':
        comparison = new Date(a.uploadDate) - new Date(b.uploadDate);
        break;
      case 'lastModified':
        comparison = new Date(a.lastModified) - new Date(b.lastModified);
        break;
      case 'uploadedBy':
        comparison = a.uploadedBy.localeCompare(b.uploadedBy);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'department':
        if (a.department && b.department) {
          comparison = a.department.localeCompare(b.department);
        } else if (a.department) {
          comparison = -1;
        } else if (b.department) {
          comparison = 1;
        }
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-5670" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-5671" />;
  };
  // Render grid view
  if (viewMode === 'grid') {
    return <div className="p-4" data-id="element-5672">
        {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-5673">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" data-id="element-5674"></div>
            <span className="ml-3 text-sm text-gray-500" data-id="element-5675">Loading files...</span>
          </div> : !files || files.length === 0 ? <div className="text-center py-10" data-id="element-5676">
            <p className="text-gray-500" data-id="element-5677">
              No files found matching your criteria.
            </p>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-5678">
            {sortedFiles.map(file => <div key={file.id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer group" onClick={() => onFileSelect(file)} data-id="element-5679">
                {/* Thumbnail Section */}
                <div className="relative w-full h-48 bg-gray-100 overflow-hidden" data-id="element-5680">
                  {file.thumbnailUrl ? <img src={file.thumbnailUrl} alt={file.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling.style.display = 'flex';
            }} data-id="element-5681" /> : null}
                  <div className={`${file.thumbnailUrl ? 'hidden' : 'flex'} w-full h-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100`} data-id="element-5682">
                    <div className="text-center" data-id="element-5683">
                      {getFileTypeIcon(file.type)}
                      <div className="mt-2 text-xs font-medium text-gray-500" data-id="element-5684">
                        {file.type.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-5685">
                    <div className="flex space-x-2" data-id="element-5686">
                      <button onClick={e => {
                  e.stopPropagation();
                  onFileSelect(file);
                }} className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors" title="View details" data-id="element-5687">
                        <EyeIcon className="h-5 w-5 text-gray-700" data-id="element-5688" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  onDownload(file.id);
                }} className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors" title="Download" data-id="element-5689">
                        <DownloadIcon className="h-5 w-5 text-gray-700" data-id="element-5690" />
                      </button>
                    </div>
                  </div>
                  {/* File type badge */}
                  <div className="absolute top-2 right-2" data-id="element-5691">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white shadow-sm" data-id="element-5692">
                      {file.type.toUpperCase()}
                    </span>
                  </div>
                </div>
                {/* File Info Section */}
                <div className="p-4" data-id="element-5693">
                  <div className="mb-2" data-id="element-5694">
                    <div className="text-sm font-medium text-gray-900 truncate" title={file.name} data-id="element-5695">
                      {file.name}
                    </div>
                    <div className="mt-1 text-xs text-gray-500" data-id="element-5696">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                  <div className="space-y-1" data-id="element-5697">
                    <div className="flex items-center text-xs text-gray-500" data-id="element-5698">
                      <UserIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-5699" />
                      <span className="truncate" data-id="element-5700">{file.uploadedBy}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-5701">
                      <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-5702" />
                      <span data-id="element-5703">{formatDate(file.uploadDate)}</span>
                    </div>
                  </div>
                  {/* File relationships */}
                  {(file.parentFiles && file.parentFiles.length > 0 || file.childFiles && file.childFiles.length > 0 || file.linkedEntities && file.linkedEntities.length > 0) && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-5704">
                      {file.parentFiles && file.parentFiles.length > 0 && <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-5705">
                          <FolderTreeIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5706" />
                          <span data-id="element-5707">{file.parentFiles.length} parent(s)</span>
                        </div>}
                      {file.childFiles && file.childFiles.length > 0 && <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-5708">
                          <FileSymlinkIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5709" />
                          <span data-id="element-5710">{file.childFiles.length} child(ren)</span>
                        </div>}
                      {file.linkedEntities && file.linkedEntities.length > 0 && <div className="flex items-center text-xs text-gray-500" data-id="element-5711">
                            <LinkIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5712" />
                            <span data-id="element-5713">{file.linkedEntities.length} linked</span>
                          </div>}
                    </div>}
                  {/* Tags */}
                  {file.tags && file.tags.length > 0 && <div className="mt-3 flex flex-wrap gap-1" data-id="element-5714">
                      {file.tags.slice(0, 2).map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5715">
                          <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5716" />
                          {tag}
                        </span>)}
                      {file.tags.length > 2 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5717">
                          +{file.tags.length - 2}
                        </span>}
                    </div>}
                  {/* Admin info */}
                  {isAdminView && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-5718">
                      <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-5719">
                        <DatabaseIcon className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" data-id="element-5720" />
                        <span className="truncate" title={file.storageLocation} data-id="element-5721">
                          {file.storageLocation.split('://')[0]}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500" data-id="element-5722">
                        <ShieldIcon className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" data-id="element-5723" />
                        <span data-id="element-5724">{file.accessLevel}</span>
                      </div>
                    </div>}
                </div>
              </div>)}
          </div>}
      </div>;
  }
  // Render list view (default)
  return <div className="overflow-x-auto" data-id="element-5725">
      {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-5726">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" data-id="element-5727"></div>
          <span className="ml-3 text-sm text-gray-500" data-id="element-5728">Loading files...</span>
        </div> : !files || files.length === 0 ? <div className="text-center py-10" data-id="element-5729">
          <p className="text-gray-500" data-id="element-5730">
            No files found matching your criteria.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-5731">
          <thead className="bg-gray-50" data-id="element-5732">
            <tr data-id="element-5733">
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-5734">
                <div className="flex items-center" data-id="element-5735">
                  File Name {renderSortIndicator('name')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')} data-id="element-5736">
                <div className="flex items-center" data-id="element-5737">
                  Type {renderSortIndicator('type')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('size')} data-id="element-5738">
                <div className="flex items-center" data-id="element-5739">
                  Size {renderSortIndicator('size')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('uploadDate')} data-id="element-5740">
                <div className="flex items-center" data-id="element-5741">
                  Uploaded {renderSortIndicator('uploadDate')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('uploadedBy')} data-id="element-5742">
                <div className="flex items-center" data-id="element-5743">
                  By {renderSortIndicator('uploadedBy')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5744">
                Relationships
              </th>
              {isAdminView && <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5745">
                  Storage
                </th>}
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5746">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-5747">
            {sortedFiles.map(file => <tr key={file.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onFileSelect(file)} data-id="element-5748">
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-5749">
                  <div className="flex items-center" data-id="element-5750">
                    {getFileTypeIcon(file.type)}
                    <div className="ml-2" data-id="element-5751">
                      <div className="text-sm font-medium text-gray-900" data-id="element-5752">
                        {file.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-5753">
                        {file.department && `${file.department}`}
                        {file.tags && file.tags.length > 0 && <span className="ml-1 inline-flex items-center" data-id="element-5754">
                            <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5755" />
                            {file.tags.slice(0, 2).join(', ')}
                            {file.tags.length > 2 && ` +${file.tags.length - 2}`}
                          </span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-5756">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5757">
                    {file.type}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5758">
                  {formatFileSize(file.size)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5759">
                  <div className="flex items-center" data-id="element-5760">
                    <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-5761" />
                    {formatDate(file.uploadDate)}
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-5762">
                    Last modified: {formatDate(file.lastModified)}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5763">
                  <div className="flex items-center" data-id="element-5764">
                    <UserIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-5765" />
                    {file.uploadedBy}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5766">
                  <div className="space-y-1" data-id="element-5767">
                    {file.parentFiles && file.parentFiles.length > 0 && <div className="flex items-center" data-id="element-5768">
                        <FolderTreeIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-5769" />
                        <span data-id="element-5770">{file.parentFiles.length} parent(s)</span>
                      </div>}
                    {file.childFiles && file.childFiles.length > 0 && <div className="flex items-center" data-id="element-5771">
                        <FileSymlinkIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-5772" />
                        <span data-id="element-5773">{file.childFiles.length} child(ren)</span>
                      </div>}
                    {file.linkedEntities && file.linkedEntities.length > 0 && <div className="flex items-center" data-id="element-5774">
                        <LinkIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-5775" />
                        <span data-id="element-5776">{file.linkedEntities.length} linked</span>
                      </div>}
                    {(!file.parentFiles || file.parentFiles.length === 0) && (!file.childFiles || file.childFiles.length === 0) && (!file.linkedEntities || file.linkedEntities.length === 0) && <span className="text-gray-400 italic" data-id="element-5777">None</span>}
                  </div>
                </td>
                {isAdminView && <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5778">
                    <div className="truncate max-w-xs" title={file.storageLocation} data-id="element-5779">
                      <DatabaseIcon className="h-4 w-4 inline mr-1 text-gray-400" data-id="element-5780" />
                      {file.storageLocation.split('://')[0]}
                    </div>
                    <div className="text-xs text-gray-400 mt-1" data-id="element-5781">
                      <ShieldIcon className="h-3 w-3 inline mr-1" data-id="element-5782" />
                      {file.accessLevel}
                    </div>
                  </td>}
                <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5783">
                  <div className="flex justify-end space-x-2" data-id="element-5784">
                    <button onClick={e => {
                e.stopPropagation();
                onFileSelect(file);
              }} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-5785">
                      <EyeIcon className="h-4 w-4" data-id="element-5786" />
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                onDownload(file.id);
              }} className="text-indigo-600 hover:text-indigo-900" title="Download" data-id="element-5787">
                      <DownloadIcon className="h-4 w-4" data-id="element-5788" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>}
    </div>;
}